import { useEffect, useState, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { analytics } from '@/lib/analytics'
import { env } from '@/env'
import { clearDevSession, getDevSession, onDevSessionChange, type DevSession } from '@/lib/devSession'
import type { Session, User, Provider } from '@supabase/supabase-js'

/* ------------------------------------------------------------------ */
/*  useAuth — wraps Supabase auth for React                            */
/*  Subscribes to onAuthStateChange for reactive session updates       */
/*  See ARCHITECTURE.md §5                                             */
/* ------------------------------------------------------------------ */

export interface UseAuthReturn {
  session: Session | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  signInWithOtp: (email: string) => Promise<{ error: Error | null }>
  signInWithOAuth: (provider: Provider) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
}

function makeDevSession(d: DevSession): Session {
  // Minimal synthetic session shape for the parts of the app that read session.user.
  // Tokens are obviously fake — never sent anywhere real in placeholder mode.
  const user = {
    id: d.userId,
    aud: 'authenticated',
    email: d.email,
    app_metadata: { provider: 'dev' },
    user_metadata: { full_name: d.fullName },
    created_at: new Date().toISOString(),
    role: 'authenticated',
  } as unknown as User
  return {
    access_token: 'dev-access-token',
    refresh_token: 'dev-refresh-token',
    token_type: 'bearer',
    expires_in: 3600,
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    user,
  } as Session
}

export function useAuth(): UseAuthReturn {
  const [session, setSession] = useState<Session | null>(null)
  // Skip the initial-load spinner entirely in placeholder mode — there's no
  // backend round-trip to wait for, and the useEffect will still settle state.
  const [isLoading, setIsLoading] = useState(!env.IS_PLACEHOLDER_BACKEND)
  const [devSession, setDevSessionState] = useState<DevSession | null>(() =>
    env.IS_PLACEHOLDER_BACKEND ? getDevSession() : null,
  )
  const queryClient = useQueryClient()

  useEffect(() => {
    if (env.IS_PLACEHOLDER_BACKEND) {
      // Skip real Supabase subscriptions when no backend is configured.
      setIsLoading(false)
      const unsubscribe = onDevSessionChange(() => {
        setDevSessionState(getDevSession())
      })
      return unsubscribe
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s)
      setIsLoading(false)
    })

    // Subscribe to auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s)
      setIsLoading(false)

      // Track auth events
      if (event === 'SIGNED_IN' && s?.user) {
        void analytics.identify(s.user.id)
        void analytics.capture('auth_signed_in', { method: s.user.app_metadata?.provider ?? 'email' })
      } else if (event === 'SIGNED_OUT') {
        void analytics.capture('auth_signed_out')
        void analytics.reset()
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Effective session — synthetic dev session takes precedence in placeholder mode.
  const effectiveSession = useMemo(() => {
    if (env.IS_PLACEHOLDER_BACKEND && devSession) return makeDevSession(devSession)
    return session
  }, [devSession, session])

  const signInWithOtp = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    return { error: error ? new Error(error.message) : null }
  }, [])

  const signInWithOAuth = useCallback(async (provider: Provider) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    return { error: error ? new Error(error.message) : null }
  }, [])

  const signOut = useCallback(async () => {
    if (env.IS_PLACEHOLDER_BACKEND) {
      clearDevSession()
      setDevSessionState(null)
      queryClient.clear()
      return
    }
    await supabase.auth.signOut()
    setSession(null)
    // Clear all cached data from previous user to prevent stale data leaking
    queryClient.clear()
  }, [queryClient])

  return {
    session: effectiveSession,
    user: effectiveSession?.user ?? null,
    isAuthenticated: !!effectiveSession,
    isLoading,
    signInWithOtp,
    signInWithOAuth,
    signOut,
  }
}
